import { useMemo } from 'react'
import styles from './ProducerSimulationBox.module.scss'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { Check } from 'components/producers/icons/Icons'
import PromoCode from 'components/promo-code/PromoCode'
import { ProducerSimulationBoxProps } from './types'
import { PopulatedLocation } from 'components/producers/new-producer-map/types'
import { getCapacityState, isReserved } from 'utils/producers'
import { routes } from 'utils/routes'
import { setSelectedProducer } from 'store/app/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import Link from 'components/link/Link'
import { Button } from '@boltenergy-be/design-system'

const ProducerSimulationBox = ({ boxClass, highlighted, producer, sticky, openModal, hide, showPromoCode }: ProducerSimulationBoxProps) => {
  // Redux store
  const { inputs, flows, propositions } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // Next router
  const router = useRouter()
  const { locale } = router

  // i18n
  const { t } = useTranslation('producer')

  // Constants
  const populatedLocation = useMemo<PopulatedLocation[]>(
    () =>
      producer.locations.map((location) => ({
        id: producer.id,
        name: producer.name,
        ...location
      })),
    [producer.id, producer.locations, producer.name]
  )
  const allLocationsSoldOut = useMemo<boolean>(
    () => populatedLocation.every((location) => getCapacityState(location, inputs.simulation, flows.simulation.didSimulation).soldOut),
    [populatedLocation, inputs.simulation, flows.simulation.didSimulation]
  )
  const allLocationsReserved = useMemo<boolean>(() => populatedLocation.every((location) => isReserved(location)), [populatedLocation])

  // Handles the click on the CTA
  const handleClick = () => {
    // If producer has multiple locations, open the modal
    if (producer.locations.length > 1) {
      openModal()
    } else {
      if (propositions?.selectedProposition) {
        return router.push(routes(locale, producer.slug).registration)
      } else {
        dispatch(setSelectedProducer({ producerSlug: producer.slug }))
        return router.push(routes(locale).simulation)
      }
    }
  }

  return (
    <div className={classNames(styles.box, [boxClass], { [styles.highlighted]: highlighted, [styles.sticky]: sticky })}>
      {!hide?.title && <h3>{t('simulationBox.buyFrom', 'Koop je energie bij {{ name }}', { name: producer.name[locale] })}</h3>}

      {/* PROMO CODE */}
      {showPromoCode && <PromoCode />}

      {/* CTA */}
      {!hide?.cta && (
        <>
          {allLocationsSoldOut || allLocationsReserved ? (
            <Button disabled className={styles.cta}>
              {allLocationsSoldOut ? t('soldOut', 'Uitverkocht') : t('reserved', 'Gereserveerd')}
            </Button>
          ) : (
            <Button onClick={handleClick} className={styles.cta}>
              {t('simulationBox.choseThisProducer', 'Kies deze energie-opwekker')}
            </Button>
          )}
        </>
      )}

      {(allLocationsSoldOut || allLocationsReserved) && (
        <Link href={routes(locale).producers} trailingIcon="chevronRight" className={styles['choose-different-btn']} size="small">
          {t('chooseDifferentProducer', 'Kies een andere energie-opwekker')}
        </Link>
      )}

      {/* ADVANTAGES */}
      {!hide?.advantages && (
        <ul className={styles.advantages}>
          <li>
            <Check /> {t('advantage1', 'Jij kiest jouw producent')}
          </li>
          <li>
            <Check /> {t('advantage2', '100% lokaal')}
          </li>
          <li>
            <Check /> {t('advantage3', 'Echte duurzame energie')}
          </li>
        </ul>
      )}
    </div>
  )
}

export default ProducerSimulationBox
